import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, media } from "@/lib/api";
import { RichEditor } from "@/components/admin/RichEditor";
import { ImageUpload } from "@/components/admin/ImageUpload";

const empty = { name: "", city_id: "", image: "", image_width: 100, content: "" };
const inputCls = "w-full border border-stone-300 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-crimson/30 bg-white";

const NewCity = ({ onCreated }) => {
  const [name, setName] = useState("");
  const add = async () => {
    if (!name.trim()) return;
    try {
      const { data } = await api.post("/admin/cities", { name });
      toast.success(`City "${data.name}" added`);
      onCreated(data);
      setName("");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="flex gap-2 mt-2">
      <input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder="New city name" className={inputCls} data-testid="listing-new-city-input" />
      <button type="button" onClick={add} className="shrink-0 text-sm bg-charcoal text-white rounded-md px-3" data-testid="listing-new-city-save">Add</button>
    </div>
  );
};

export const ListingDialog = ({ open, hotel, cities, onClose, onSaved, onCityAdded }) => {
  const [form, setForm] = useState(empty);
  const [addingCity, setAddingCity] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(hotel ? { name: hotel.name, city_id: hotel.city_id, image: hotel.image || "", image_width: hotel.image_width || 100, content: hotel.content || "" } : empty);
      setAddingCity(false);
    }
  }, [open, hotel]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.city_id) return toast.error("Hotel name and city are required");
    setBusy(true);
    try {
      if (hotel) await api.put(`/admin/hotels/${hotel.id}`, form);
      else await api.post("/admin/hotels", form);
      toast.success(hotel ? "Listing updated" : "Listing created");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="admin-root max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="listing-dialog">
        <DialogHeader><DialogTitle>{hotel ? "Edit Listing" : "New Listing"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-5">
          <div>
            <label className="text-sm font-bold block mb-1">Hotel Name</label>
            <input value={form.name} onChange={(e) => set("name")(e.target.value)} className={inputCls} data-testid="listing-name-input" />
          </div>
          <div>
            <label className="text-sm font-bold block mb-1">City</label>
            <div className="flex gap-2">
              <select value={form.city_id} onChange={(e) => set("city_id")(e.target.value)} className={inputCls} data-testid="listing-city-select">
                <option value="">Select a city…</option>
                {cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <button type="button" onClick={() => setAddingCity((v) => !v)} className="shrink-0 inline-flex items-center gap-1 text-sm border border-stone-300 rounded-md px-3 hover:bg-stone-50" data-testid="listing-add-city-toggle"><Plus className="w-4 h-4" />New city</button>
            </div>
            {addingCity && <NewCity onCreated={(c) => { onCityAdded(c); set("city_id")(c.id); setAddingCity(false); }} />}
          </div>
          <div>
            <label className="text-sm font-bold block mb-1">Image</label>
            <ImageUpload value={form.image} onChange={set("image")} testid="listing-image" />
            {form.image && (
              <div className="mt-4 border border-stone-200 rounded-md p-4 bg-stone-50" data-testid="listing-image-size">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="font-bold">Image size on page</span>
                  <span className="font-bold text-crimson" data-testid="listing-image-width-value">{form.image_width}%</span>
                </div>
                <input type="range" min="10" max="100" step="5" value={form.image_width} onChange={(e) => set("image_width")(Number(e.target.value))} className="w-full accent-[#C8102E]" data-testid="listing-image-width-slider" />
                <div className="mt-3 bg-white border border-dashed border-stone-300 rounded p-2">
                  <img src={media(form.image)} alt="" className="block h-auto mx-auto" style={{ width: `${form.image_width}%` }} data-testid="listing-image-size-preview" />
                </div>
              </div>
            )}
          </div>
          <div>
            <label className="text-sm font-bold block mb-1">Content</label>
            <p className="text-xs text-stone-500 mb-2">Leave empty to show the default "listed in IHD 2026" text.</p>
            <RichEditor value={form.content} onChange={set("content")} testid="listing-content-editor" minHeight={200} maxHeight={380} align />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="text-sm border border-stone-300 rounded-md px-4 py-2" data-testid="listing-cancel-btn">Cancel</button>
            <button disabled={busy} className="text-sm bg-crimson hover:bg-crimson-hover text-white font-bold rounded-md px-5 py-2 inline-flex items-center gap-2 disabled:opacity-60" data-testid="listing-save-btn">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}Save Listing
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
